import './AdvancedTable.scss';
import type {ReactElement} from "react";
import React from "react";
import * as d3 from "d3";
import type {AxiosResponse} from "axios";
import {
    LinearProgress,
    Paper,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography
} from "@mui/material";
import {Info} from "@mui/icons-material";
import {CurrencyComponent} from "../currency-component/CurrencyComponent";
import type {AdvancedDashboardPageResponse, AdvancedSupplierBreakdownData} from "../../services/ApiTypes";
import {observer} from "mobx-react-lite";
import {UNCATEGORIZED_LABEL, UNCATEGORIZED_VALUE} from "../../constants";
import {getBusinessUnitIdHack, getSupplierIdHack} from "../../stores/hacks/display";
import {useStores} from "../../stores";
import {getMinMax} from "../../utils/js-utils";
import type {HeaderSpecification} from "../../pages/advanced/classes/HeaderSpecification";
import {ADV_DASH_PAGE_SIZE_OPTIONS} from "../../stores/managers/BagAdvancedInsightsManager.tsx";
import type {RequestManager} from "../../stores/managers/RequestManager.tsx";
import {AccordionLight} from "~/components/_tiny/AccordionLight.tsx";

type Props = {
    headers: Array<HeaderSpecification>,
    className?: string
    requestManager: RequestManager<any, AxiosResponse<AdvancedDashboardPageResponse<any>>>
    page: number
    onPageChange: (value: number) => void
    perPage: number
    onPerPageChange: (value: number) => void
    pageSizeOptions?: Array<number>
    breakdownFilterChanged?: (item?: AdvancedSupplierBreakdownData) => void
    container?: boolean
    ordering?: string
    rowSpan?: boolean
    loading?: boolean
};
export const AdvancedTable: React.FC<Props> = observer((
    {
        headers,
        className,
        requestManager,
        page,
        onPageChange,
        perPage,
        onPerPageChange,
        pageSizeOptions,
        breakdownFilterChanged,
        container,
        ordering,
        rowSpan,
        loading
    }) => {
    const {bagStore} = useStores();
    const containerComponent = container ? Paper : 'div';
    const [rowsPerPageOptions] = React.useState(pageSizeOptions ?? ADV_DASH_PAGE_SIZE_OPTIONS);
    const isLoading = loading ?? requestManager.busy;

    const colorMapMinMax = new Map<string, [number, number]>()
    headers.filter(h => h.type === 'currency').forEach(h => {
        let [min, max] = getMinMax((requestManager.result?.data.rows ?? []).map(d => d[h.key]), -1, 1);
        const colorKey = h.colorMapKey ?? h.key;
        const prev = colorMapMinMax.get(colorKey)
        if (prev) {
            min = Math.min(min, prev[0]);
            max = Math.max(max, prev[1]);
        }
        colorMapMinMax.set(colorKey, [min, max]);
    });
    const colorScalesMap = new Map(Array.from(colorMapMinMax.entries())
        .map(([colorKey, [min, max]]) => [colorKey, {
            positive: d3.scaleLinear([0, max], ['whitesmoke', '#193150']),
            negative: d3.scaleLinear([0, min], ['whitesmoke', '#E62D15']),
        }])
    );

    const tableClass = 'advanced-table-wrapper'
        + (className ? ` ${className}` : '')
        + (isLoading ? ' loading' : '');

    function order(headerKey: string, ordering?: string) {
        if (ordering === undefined) return false;
        if (ordering === headerKey) return 'asc';
        if (ordering.startsWith('-') && ordering.substring(1) === headerKey) return 'desc';
        return false;
    }

    function getCellSpec(header: HeaderSpecification, item: any): [any, string, string, React.ReactNode] {
        let value;
        if (header.key === 's_id' || header.key === 's_customer_id') {
            value = getSupplierIdHack(item);
        } else if (header.key === 'bu_id') {
            value = getBusinessUnitIdHack(item);
        } else if (header.key === 'p_src_databag_id') {
            value = bagStore.getBagName(item['p_src_databag_id']);
        } else {
            value = item[header.key]
        }
        let cellNumberClass = "";
        const className = "";
        let cellContent: ReactElement
        switch (header.type) {
            case 'currency':
                cellContent = <CurrencyComponent v={value}/>;
                cellNumberClass = "fit-number";
                break;
            case '2_decimal':
                cellContent = <>{(Math.round(value * 100) / 100).toFixed(2)}</>;
                break;
            case 'category':
                cellContent = <>{value === UNCATEGORIZED_VALUE ? UNCATEGORIZED_LABEL : value}</>;
                break;
            case 'string[]':
                if (Array.isArray(value)) {
                    const items = value as string[];
                    if (header.showMore && items.length > header.showMore) {
                        const remainingN = items.length - header.showMore;
                        const preview = `${items.slice(0, header.showMore).join(', ')}, ... (${remainingN} more)`;
                        const rest = items.slice(header.showMore).join(', ');
                        cellContent = <AccordionLight summary={preview}>{rest}</AccordionLight>;
                    } else {
                        cellContent = <>{items.join(', ')}</>;
                    }
                } else {
                    cellContent = <>{value}</>;
                }
                break;
            case 'custom':
            default:
                if (header.render) {
                    cellContent = <>{header.render(item)}</>;
                } else {
                    cellContent = <>{value}</>;
                }
                break;
        }
        return [value, cellNumberClass, className, cellContent];
    }

    function getRowSpan(header: HeaderSpecification, value: any, nextIndex: number, stopAtTwo = false) {
        if (rowSpan && (header.type === 'category' || header.type === 'string[]') &&
            nextIndex < (requestManager.result?.data.rows.length ?? 0) &&
            requestManager.result &&
            value === getCellSpec(header, requestManager.result[nextIndex])[0]) {
            if (stopAtTwo) return 2;
            return getRowSpan(header, value, nextIndex + 1, stopAtTwo) + 1;
        }
        return 1;
    }

    const [helpAnchorElements, setHelpAnchorElements] = React.useState<Record<string, Element>>({});
    const handlePopoverOpen = (colum: string, event: React.MouseEvent) => {
        // Close the others
        setHelpAnchorElements({[colum]: event.currentTarget});
    };
    const handlePopoverClose = (colum: string) => {
        const objs = {...helpAnchorElements};
        delete objs[colum];
        setHelpAnchorElements(objs);
    };

    return <TableContainer component={containerComponent} className={tableClass}>
        <Table cellPadding={0}>
            <TableHead>
                <TableRow>
                    {headers.map(header => {
                        const anchorEl = helpAnchorElements[header.key];
                        const open = Boolean(anchorEl);
                        const key = header.key;
                        const hasPopover = Boolean(header.help);
                        const popoverIcon = <>{hasPopover && <Info
                            sx={{ml: .5}}
                            onMouseEnter={hasPopover ? e => handlePopoverOpen(key, e) : undefined}
                            onMouseLeave={hasPopover ? () => handlePopoverClose(key) : undefined}
                            fontSize="small"
                        />}</>
                        return <>
                            {header.onOrder
                                ? <TableCell key={key} sortDirection={order(key, ordering)}>
                                    <TableSortLabel
                                        active={order(key, ordering) !== false}
                                        direction={order(key, ordering) || undefined}
                                        onClick={header.onOrder}>
                                        {header.label}
                                        {popoverIcon}
                                    </TableSortLabel>
                                </TableCell>
                                : <TableCell key={key}>
                                    {header.label}
                                    {popoverIcon}
                                </TableCell>}
                            {hasPopover && <Popover
                                id={`mouse-over-popover-${key}`}
                                sx={{pointerEvents: 'none'}}
                                open={open}
                                anchorEl={anchorEl}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center',
                                }}
                                transformOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center',
                                }}
                                onClose={() => handlePopoverClose(key)}
                                disableRestoreFocus
                            >
                                {header.help}
                            </Popover>}
                        </>;
                    })}
                </TableRow>
                <tr className={'progress-row' + (isLoading ? ' loading' : '')}>
                    <td colSpan={headers.length}>
                        {isLoading && <LinearProgress variant="indeterminate"/>}
                    </td>
                </tr>
            </TableHead>
            <TableBody>
                {requestManager.result && (requestManager.result.data.rows ?? []).map((item, item_index) => {
                    const onClick = () => {
                        // setPage(0);
                        if (requestManager.result!.data.rows.length === 1)
                            // If there is only one item, click should remove filter
                            breakdownFilterChanged?.(undefined)
                        else
                            breakdownFilterChanged?.(item)
                    };
                    return <TableRow
                        hover
                        key={JSON.stringify(item)} // TODO: This is not a good key but okay for small cells
                        className={'advanced-table-row row-' + (item_index % 2 ? 'odd' : 'even')}>
                        {headers.map(header => {
                            const cellSpecs = getCellSpec(header, item);
                            const style = {};
                            const colorKey = header.colorMapKey ?? header.key;
                            const colorScale = colorScalesMap.get(colorKey)
                            if (colorScale) {
                                const {positive, negative} = colorScale;
                                const bgColor = cellSpecs[0] >= 0 ? positive(cellSpecs[0]) : negative(cellSpecs[0]);
                                const fgColor = d3.hsl(bgColor).l > 0.5 ? 'black' : 'white';
                                style['backgroundColor'] = bgColor;
                                style['color'] = fgColor;
                            }
                            // Find if item should have rowSpan, and use pivoting if it does
                            if (item_index > 0 && getRowSpan(header, cellSpecs[0], item_index - 1, true) > 1)
                                return <></>;
                            const row_span = getRowSpan(header, cellSpecs[0], item_index + 1);
                            const clickable = header.type !== 'action';
                            return <TableCell
                                key={header.key}
                                rowSpan={row_span}
                                className={cellSpecs[2] + " advanced-table-cell" + ((row_span > 1) ? ' row-span' : '') + (clickable ? ' clickable' : '')}
                                style={style}
                                onClick={clickable ? onClick : undefined}>
                                <Typography component="div" className={cellSpecs[1]} style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    // CAT-1900 Commenting this out would cause long texts to break into multiple lines if necessary
                                    // whiteSpace: 'nowrap'
                                }}>{cellSpecs[3]}</Typography>
                            </TableCell>
                        })}
                    </TableRow>;
                })}
                {requestManager.result && requestManager.result.data.rows?.length === 0 &&
                    <TableRow className="no-data">
                        <TableCell colSpan={headers.length}>
                            No data
                        </TableCell>
                    </TableRow>
                }
            </TableBody>
            <TableFooter>
                <TableRow>
                    <TablePagination
                        rowsPerPageOptions={rowsPerPageOptions}
                        colSpan={headers.length}
                        // count={data?.length || 0}
                        count={requestManager.result?.data.count ?? 0}
                        rowsPerPage={perPage}
                        page={page}
                        showFirstButton={true}
                        showLastButton={true}
                        SelectProps={{
                            inputProps: {'aria-label': 'rows per page'},
                            native: true,
                        }}
                        onPageChange={(_e, p) => {
                            onPageChange(p)
                        }}
                        onRowsPerPageChange={(e) => {
                            onPerPageChange(Number(e.target.value))
                        }}
                    />
                </TableRow>
            </TableFooter>
        </Table>
    </TableContainer>
})
